/**
 * Utilidades para mapear datos de un árbol existente (respuesta de getPropertiesByID)
 * al formato de initialValues de Formik en el wizard V2.
 */

/**
 * Transforma la respuesta de getPropertiesByID(treeId) al shape de Formik.
 *
 * Nota: Los inconvenientes de categorías 1 y 7 (calidad/riesgos) ya son
 * pre-llenados por setItemsFormik({ properties: treeData }), por lo que
 * esta función se enfoca en atributos, campos generales y extras.
 *
 * @param {Object} treeData       - Respuesta de getPropertiesByID(treeId)
 * @param {Object} formikBase     - INITIAL_FORMIK_STATE generado por setItemsFormik
 * @param {Object} zoneTypesData  - fullData de getZoneTypesAll() → { [id]: { nombre, ... } }
 * @param {Object} siteTypesData  - fullData de getSiteTypesAll() → { [id]: { nombre, ... } }
 * @returns {Object} initialValues listos para Formik
 */
export function mapTreeDataToFormikValues(
  treeData,
  formikBase,
  zoneTypesData = {},
  siteTypesData = {},
) {
  if (!treeData || !formikBase) return formikBase

  const attributes = Array.isArray(treeData.attributes) ? treeData.attributes : []
  const site = treeData.site ?? {}

  // Helper: buscar valor de un atributo por su key
  const findAttr = key => {
    const found = attributes.find(a => a.metaFeature?.key === key)
    return found?.value ?? ''
  }

  // ── Coordenadas y altitud ──
  const longX = String(findAttr('longX') ?? '')
  const latY = String(findAttr('latY') ?? '')
  const altitud = Number(findAttr('altitud')) || ''

  // ── Dasométricos ──
  const dap = String(findAttr('dap') ?? '')
  const cap = String(findAttr('cap') ?? '')
  const ht = String(findAttr('ht') ?? '')
  const hc = String(findAttr('hc') ?? '')
  const copaNS = String(findAttr('copaNS') ?? '')
  const copaEW = String(findAttr('copaEW') ?? '')
  const bifurcaciones = String(findAttr('bifurcaciones') ?? '')
  const inclArbol = String(findAttr('inclArbol') ?? '')
  const compInclArbol = String(findAttr('compInclArbol') ?? '')
  const fusteRectitud = findAttr('fusteRectitud') ?? ''
  const espaciamiento = findAttr('espaciamiento') ?? ''

  // ── Fitosanitario ──
  const follaje = String(findAttr('follaje') ?? '')
  const madurez = findAttr('madurez') ?? ''
  const floracion = findAttr('floracion') ?? ''
  const fructificacion = findAttr('fructificacion') ?? ''

  // ── Campos generales ──
  const alive_ = treeData.alive ? '1' : '0'
  const treeVerified_ = treeData.treeVerified ? '1' : '0'
  const estadoRegistro = _normalizeEstadoRegistro(
    treeData.estadoRegistro,
    treeData.treeVerified,
  )
  const observacionesRevision =
    estadoRegistro === 'en_revision'
      ? _normalizeRevisionObservations(treeData.observacionesRevision)
      : []
  const recorded_ = treeData.recorded ?? ''
  const commit_ = treeData.commit ?? ''

  // ── Tipo de zona/sitio: mapear de nombre a ID ──
  // site.zoneType y site.siteType son nombres (strings) del backend.
  // Los campos Formik typeSite_ y sitePlace_ esperan IDs (strings).
  const typeSite_ = _findIdByName(zoneTypesData, site.zoneType) ?? ''
  const sitePlace_ = _findIdByName(siteTypesData, site.siteType) ?? ''

  // ── Campos Autocomplete ──
  // Los componentes FamilySpecie y SiteSeccionAcronym leen de values[name] de Formik
  // para sincronizar su estado local de display. Si estos campos quedan vacíos en
  // initialValues, los efectos de sync limpian la selección mostrada aunque Redux
  // tenga los datos correctos.
  const family_ = treeData.specie?.family?.id ? String(treeData.specie.family.id) : ''
  const specie_ = treeData.specie?.id ? String(treeData.specie.id) : ''
  const site_ = treeData.site?.name ?? ''
  const siteAcronym_ = treeData.site?.siteAcronym ?? ''
  const section_ = treeData.site?.section ?? ''

  // ── Plus Code ──
  // modoCodigoPlusCode = true solo si el código es PLUS_CODE
  const modoCodigoPlusCode = treeData.codeMode === 'PLUS_CODE'
  // codeAction: intención del usuario respecto al código en edición.
  // Default 'keep' para cualquier modo no-PLUS_CODE (no regenerar salvo elección explícita).
  const NON_PLUS_MODES = ['STRUCTURED', 'STRUCTURED_LEGACY', 'MANUAL']
  const codeAction = NON_PLUS_MODES.includes(treeData.codeMode) ? 'keep' : null

  // ── Referencia de ubicación ──
  const referenciaUbicacion_ = treeData.referencia_ubicacion ?? treeData.locationReference ?? ''

  // ── Prioridad ──
  const prioridadAtencion = _pickFirstDefined(
    treeData.prioridadAtencion,
    treeData.prioridad_atencion,
    treeData.attentionPriority,
  ) ?? ''

  return {
    ...formikBase, // mantiene todos los campos con sus defaults (incluye inconveniences)
    // Sobrescribir con datos reales del árbol:
    longX,
    latY,
    altitud,
    alive_,
    treeVerified_,
    estadoRegistro,
    observacionesRevision,
    // Confirmación de corrección aplicada por el personal de campo (solo lectura para el admin).
    correccionAplicadaAt: treeData.correccionAplicadaAt ?? null,
    correccionAplicadaPor: treeData.correccionAplicadaPor ?? null,
    correccionAplicadaPorNombre: treeData.correccionAplicadaPorNombre ?? null,
    recorded_,
    commit_,
    typeSite_,
    sitePlace_,
    family_,
    specie_,
    site_,
    siteAcronym_,
    section_,
    dap,
    cap,
    ht,
    hc,
    copaNS,
    copaEW,
    bifurcaciones,
    inclArbol,
    compInclArbol,
    fusteRectitud,
    espaciamiento,
    follaje,
    madurez,
    floracion,
    fructificacion,
    modoCodigoPlusCode,
    codeAction,
    referenciaUbicacion_,
    prioridadAtencion,
    codigoManual: '', // nunca se pre-rellena en edit mode
  }
}

/**
 * Construye el estado inicial de Redux (autocompletes) desde treeData.
 * Se dispatcha después de que setItemsFormik resuelve para pre-llenar
 * los campos autocomplete (especie, sitio, sección, acrónimo, pseudónimos).
 *
 * @param {Object} treeData - Respuesta de getPropertiesByID
 * @returns {{ specieField, siteField, siteAcronym, sectionField, pseudonymField }}
 */
export function mapTreeDataToAutocompleteState(treeData) {
  if (!treeData) return null

  const { specie = {}, site = {} } = treeData

  const specieField = specie?.id
    ? {
        error: false,
        selected: {
          id: String(specie.id),
          label: specie.name ?? '',
        },
      }
    : { error: false, selected: null }

  // siteField.selected es el nombre del sitio (string)
  const siteField = site?.name
    ? { error: false, selected: site.name }
    : { error: false, selected: null }

  // siteAcronym.selected es el acrónimo del sitio (string)
  const siteAcronym = site?.siteAcronym
    ? { error: false, selected: site.siteAcronym }
    : { error: false, selected: null }

  // sectionField.selected es el código de sección (string)
  const sectionField = site?.section
    ? { error: false, selected: site.section }
    : { error: false, selected: null }

  // pseudonymField.selected es un array de { id, name }
  const pseudonymField = {
    error: false,
    selected: Array.isArray(specie?.pseudonym)
      ? specie.pseudonym.map(p => ({ id: p.id, name: p.name }))
      : [],
  }

  return { specieField, siteField, siteAcronym, sectionField, pseudonymField }
}

// ── Helpers internos ──

/**
 * Busca el ID (como string) en un objeto { [id]: { nombre, ... } }
 * cuyo campo `nombre` coincide con el valor dado.
 */
function _findIdByName(dataMap, name) {
  if (!name || !dataMap) return ''
  const entry = Object.entries(dataMap).find(
    ([, data]) =>
      (data?.nombre ?? data?.name ?? '')
        .toLowerCase()
        .trim() === String(name).toLowerCase().trim(),
  )
  return entry ? String(entry[0]) : ''
}

function _pickFirstDefined(...values) {
  return values.find(value => value !== undefined && value !== null)
}

function _normalizeEstadoRegistro(value, treeVerified) {
  if (['no_verificado', 'en_revision', 'verificado'].includes(value)) return value
  return treeVerified ? 'verificado' : 'no_verificado'
}

function _normalizeRevisionObservations(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}
